// Boot command (EPIC #157, phase P6 — design §4).
//
// Composes the boot sequence — `/api/forecasts` load → restore decision → resume sweep — as
// ONE explicit ordered function, replacing the effect chain split across
// `useForecastPersistence` (the load effect) and App.jsx (the restore-dispatcher effect, keyed
// on `hydrationDone`) and the `bootPhase`/`hydrationDone` two-flag pair those effects raced on.
// `useBootForecasts` (the thin React binding) calls this from ONE mount effect; `setBootPhase`
// is now called from exactly one place, in this fixed order, so there is no cross-effect race
// to reason about.
//
// The restore decision's side effects (`selectForecast`, `resumeTour`) are functions this
// command doesn't define — threaded through forward refs because of render order:
// `useBootForecasts` is called before `selectForecast`/`resumeTour` exist this render (they
// depend on values `useBootForecasts` itself returns), so their owners assign the refs later —
// `useForecastSelectionActions` sets `selectForecastRef.current` during its render and
// `useOnboardingWiring` sets `resumeTourRef.current` in a layout effect — and this command
// reads `.current` inside the mount effect, which only runs after the full render has
// committed.
import { noteForecastOpen } from "../lib/telemetry/forecastOpen.js";
import { decideRestore } from "../lib/forecast/restore.js";
import { extractDistribution } from "../lib/forecast/multiway.js";
import { resolutionRightBufferFor } from "../lib/timeline/multiwayMarker.js";
import { selectLegacyForecastById } from "../lib/forecast/selectors.js";
import { ACTIVE_FORECAST_KEY } from "../lib/forecast/storageKeys.js";
import { loadForecasts } from "./loadForecasts.js";
import { resumeForecasts } from "./resumeForecasts.js";
import type { ResumeDeps } from "./resumeForecasts.js";

type AnyFn = (...args: any[]) => any;
type Ref<T = any> = { current: T };

/** The restore-decision side effects, App.jsx-owned (see module doc on the forward-ref seam). */
interface BootRefs {
  selectForecastRef: Ref<AnyFn | null>;
  resumeTourRef: Ref<AnyFn | null>;
}

export interface BootDeps extends ResumeDeps {
  refs: ResumeDeps["refs"] & BootRefs;
  ui: ResumeDeps["ui"] & {
    setPinned: AnyFn;
    setBootPhase: AnyFn;
  };
  callbacks: ResumeDeps["callbacks"] & {
    // Fired once, right after the load phase settles (success or failure) — BEFORE the
    // restore decision/resume sweep run.
    //   - `ok` (hydrationOk): true in legacy/no-auth mode too — "nothing failed." Used by the
    //     restore decision's 'none' branch for its stale-key cleanup gate.
    //   - `loaded`: true ONLY after a genuine authed, successful fetch — false in legacy mode.
    //     `useBootForecasts` assigns this to the `loadedRef` it exposes (Lane E1's
    //     compare-hydration effect in App.jsx reads it to decide resolve/prune-against-the-real-
    //     list vs. preserve-the-raw-URL-selection) and, via a `loadSucceeded` state flip, defers
    //     installing the persister to the render AFTER this same call's `setPinned` has
    //     committed — see `useBootForecasts.js` and `commands/forecastPersister.ts`. Neither of
    //     those consumers happens synchronously inside this function.
    onLoaded?: (ok: boolean, loaded: boolean) => void;
  };
}

export interface BootResult {
  ok: boolean;
  pinned: Record<string, unknown>;
}

/**
 * Restore signals read once, synchronously, at mount — see `readRestoreSignals` in
 * restore.js (still a plain-JS module; typed loosely at the boundary, decision C).
 */
export type RestoreSignals = any;

/**
 * Apply the ONE restore decision (tour > in-flight > last-viewed > none — see `decideRestore`).
 * Moved verbatim from App.jsx's restore-dispatcher effect. `loadOk` gates the 'none' branch's
 * stale-key cleanup exactly as `hydrationOkRef` did: only clear `ACTIVE_FORECAST_KEY` after a
 * genuinely successful (or legacy no-auth) load, never after a transient failure.
 */
function applyRestoreDecision(deps: BootDeps, signals: RestoreSignals, loadOk: boolean): void {
  const decision = decideRestore(signals, deps.store.getState().runsById);
  switch (decision.kind) {
    case "tour":
      // resumeTour is async and awaits (dynamic fixture import, loadConversationThread)
      // BEFORE its internal runSayerTour().catch — handle the promise here so an early
      // rejection during restore can't surface as an unhandled rejection.
      deps.refs.resumeTourRef
        .current?.(decision.tour)
        ?.catch((e: unknown) => console.error("tour resume failed:", e));
      break;
    case "reattach": {
      if (decision.runId) {
        // Last-viewed forecast still running with a traceId → reattach by run. Resolve the
        // store runId back to the legacy forecast shape selectForecast consumes, against the
        // FULL store (not the capped display list) so an old last-viewed forecast still
        // restores.
        const match = selectLegacyForecastById(deps.store.getState(), decision.runId);
        if (match) {
          // Restored at boot: a `forecast.open` from `auto` (hooks/useForecastOpenIntent.js).
          noteForecastOpen("auto", { keep: true });
          deps.refs.selectForecastRef.current?.(match, { reattach: true });
        }
      }
      // else: in-flight self-submitted → deferred to the resume sweep below, which owns
      // PENDING_FORECAST_KEY recovery + active (mirrors the deleted effect's `if(hasPending)
      // return` defer to the sweep).
      break;
    }
    case "select": {
      // Completed/settled last-viewed forecast → select (no reattach), rehydrate its thread,
      // and frame the timeline. Full-store lookup so an old last-viewed forecast still restores.
      // LegacyForecast is a pragmatically-typed open record (decision C) — cast at this
      // boundary rather than threading a narrower shape through just for this read.
      const match = selectLegacyForecastById(deps.store.getState(), decision.runId) as any;
      if (match) {
        noteForecastOpen("auto", { keep: true });
        deps.refs.selectForecastRef.current?.(match, { reattach: false });
        if (match.forecastId) {
          // Root runId === conversationId, so match.id is the convId.
          deps.callbacks.loadConversationThread(match.forecastId, match.id || null);
        }
        if (match.contract?.resolution_date) {
          // Framed EXACTLY as the CENTER button frames it — this is the RELOAD path, and it
          // was the last one still diverging. Two differences mattered, both fixed here:
          //
          //  • no `startDate`. Passing `match.createdAt` anchored the left edge at the
          //    forecast's creation instead of today, which is not what centering does.
          //  • an explicit right-hand BUFFER. `autoZoom`'s own buffer describes whatever is
          //    in `active`, and on a cold reload that is nothing — so it fell back to the
          //    narrow %-label reservation, left the frame too wide, and opened zoomed in
          //    well past CENTER. `assumeMarker: true` matches what App does while the flags
          //    are still loading (over-reserve for one paint rather than clip).
          const buckets =
            extractDistribution(match.contract, {
              allMultiway: true,
              probability: match.contract?.probability,
            })?.buckets ?? null;
          deps.refs.autoZoomRef.current?.(
            match.contract.resolution_date,
            true,
            null,
            resolutionRightBufferFor(buckets, { assumeMarker: true }),
          );
        }
      }
      break;
    }
    case "none":
    default:
      // Stale active-key cleanup — ONLY after a SUCCESSFUL load (forecast genuinely deleted,
      // or signed in as someone else, or legacy no-auth mode). On a failed load the key is
      // preserved so a transient server error doesn't erase the last-viewed restore.
      if (loadOk) {
        try {
          sessionStorage.removeItem(ACTIVE_FORECAST_KEY);
        } catch {
          /* ignore */
        }
      }
      break;
  }
}

/**
 * The boot sequence, composed explicitly: load → apply the restore decision → resume sweep.
 * `authEnabled=false` (legacy/no-Zitadel mode) skips the fetch entirely — there is no server
 * persistence to load — but still runs the restore decision + resume sweep over whatever the
 * store already holds (harmless no-ops on a store that starts empty). Only called once auth has
 * settled AND (legacy mode OR a signed-in user exists) — `useBootForecasts` owns that gate, since
 * a visibility-triggered `/api/me` refetch can flip a signed-out session to signed-in without a
 * remount, and that transition is this function's ONE call, not a re-fire.
 */
export async function bootForecasts(
  deps: BootDeps,
  { authEnabled, restoreSignals }: { authEnabled: boolean; restoreSignals: RestoreSignals },
  cancelledRef: Ref<boolean>,
): Promise<BootResult> {
  let ok = true;
  let pinned: Record<string, unknown> = {};
  if (authEnabled) {
    try {
      const r = await loadForecasts(deps, cancelledRef);
      ok = r.ok;
      pinned = r.pinned;
    } catch (_e) {
      ok = false; // server unreachable / unexpected failure; stay with empty state
    }
  }
  if (cancelledRef.current) return { ok, pinned };
  // `ok` (hydrationOk) and `loaded` are DISTINCT signals — collapsing them into one caused a
  // real bug (Copilot review, PR #3413): `ok` defaults true in legacy/no-auth mode (nothing
  // failed — there was nothing to fetch), but `loaded` must stay false there, since it answers
  // "did a real /api/forecasts fetch actually populate the store" — the signal
  // `useBootForecasts` hands to Lane E1's compare-hydration effect to decide resolve/prune vs.
  // preserve-raw-URL-selection. Only a genuine authed, successful fetch sets it.
  deps.callbacks.onLoaded?.(ok, authEnabled && ok);
  // Merge-not-replace: if the user added a forecast during the fetch, keep theirs (it isn't in
  // the persister's baseline yet → the next persist pass POSTs it).
  deps.ui.setPinned((prev: Record<string, unknown>) => ({ ...pinned, ...prev }));
  deps.ui.setBootPhase((p: string) => (p === "loading" ? "restoring" : p));
  applyRestoreDecision(deps, restoreSignals, ok);
  deps.ui.setBootPhase("ready"); // ready on active-set, NOT on async thread/reattach completion
  if (cancelledRef.current) return { ok, pinned };
  await resumeForecasts(deps, cancelledRef, {
    // A successful authenticated load makes the hydrated store authoritative for
    // terminal/missing handle pruning. On a transient load failure, preserve those
    // handles so a later reload can recover them instead of treating absence as terminal.
    pruneMissingCompareHandles: !authEnabled || ok,
  });
  return { ok, pinned };
}
